import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProductImageGallery = ({ images = [], productName = '', selectedImageUrl = null }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copyConfirm, setCopyConfirm] = useState(false);

  // When the variant picker chooses a style (image swatch), the parent passes
  // that style's image URL down via selectedImageUrl. If it matches one of the
  // gallery images, jump to it. If it doesn't (variation images can live
  // outside the parent gallery), it gets prepended below as a virtual slot
  // so arrows still step through a single ordered list.
  React.useEffect(() => {
    if (!selectedImageUrl) return;
    const idx = images.findIndex((im) => (im?.url || im?.src) === selectedImageUrl);
    setSelectedImageIndex(idx >= 0 ? idx : 0);
  }, [selectedImageUrl, images]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = productName || 'Check this out';
  const shareImage = images?.[selectedImageIndex]?.url || images?.[0]?.url || '';

  const openShare = (url) => {
    if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener,noreferrer,width=620,height=540');
    setShowShareMenu(false);
  };

  const handleShareClick = async () => {
    // Prefer native Web Share when available (mobile + some desktops).
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl });
        return;
      } catch (err) {
        // AbortError = user explicitly cancelled the OS share sheet. Respect
        // that — don't surprise them with our dropdown after. Other errors
        // (NotAllowedError, share-permission denied, etc.) fall through to
        // the manual menu so they still have a way to share.
        if (err && (err.name === 'AbortError' || /abort|cancel/i.test(err.message || ''))) return;
      }
    }
    setShowShareMenu((v) => !v);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyConfirm(true);
      setTimeout(() => setCopyConfirm(false), 1600);
    } catch (_) {}
  };

  const shareTargets = [
    { name: 'Facebook',  icon: 'Facebook',  url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { name: 'X',         icon: 'Twitter',   url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}` },
    { name: 'Pinterest', icon: 'Pin',       url: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&media=${encodeURIComponent(shareImage)}&description=${encodeURIComponent(shareTitle)}` },
    { name: 'WhatsApp',  icon: 'MessageCircle', url: `https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}` },
    { name: 'Email',     icon: 'Mail',      url: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareTitle + ' — ' + shareUrl)}` },
  ];

  const defaultImages = [
  {
    id: 1,
    url: "https://images.unsplash.com/photo-1697840507245-e6ce44da4e4c",
    alt: 'Luxury hydrating serum bottle with golden cap on white marble surface'
  },
  {
    id: 2,
    url: "https://images.unsplash.com/photo-1664351018742-f8e28ebbd170",
    alt: 'Close-up view of serum bottle showing ingredient label and texture'
  },
  {
    id: 3,
    url: "https://images.unsplash.com/photo-1648712789194-16c56f19ff09",
    alt: 'Serum bottle with dropper applicator showing product consistency'
  },
  {
    id: 4,
    url: "https://images.unsplash.com/photo-1577058109956-67adf6edc586",
    alt: 'Product packaging box and bottle arranged with beauty accessories'
  }];


  const baseGallery = images?.length > 0 ? images : defaultImages;
  // If the variant picker pointed at an image that isn't in the parent
  // gallery, prepend it as a virtual entry so the prev/next arrows and
  // thumbnails stay in sync with selectedImageIndex (otherwise the arrows
  // would no-op when a variation image was active).
  const galleryImages =
    selectedImageUrl && !baseGallery.some((im) => (im?.url || im?.src) === selectedImageUrl)
      ? [{ id: 'variant-current', url: selectedImageUrl, alt: productName }, ...baseGallery]
      : baseGallery;
  const currentImage = galleryImages?.[selectedImageIndex] || galleryImages?.[0];

  const handlePreviousImage = () => {
    setSelectedImageIndex((prev) =>
    prev === 0 ? galleryImages?.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) =>
    prev === galleryImages?.length - 1 ? 0 : prev + 1
    );
  };

  const toggleZoom = () => {
    setIsZoomed(!isZoomed);
  };

  return (
    <div className="space-y-4">
      {/* Main Image Display */}
      <div className="relative bg-card rounded-2xl overflow-hidden border border-border shadow-elevation-1">
        <div className="aspect-square relative group">
          <Image
            src={currentImage?.url}
            alt={currentImage?.alt}
            className={`w-full h-full object-cover transition-transform duration-300 cursor-zoom-in ${
            isZoomed ? 'scale-150' : 'scale-100'}`
            }
            onClick={toggleZoom} />

          
          {/* Navigation Arrows */}
          {galleryImages?.length > 1 &&
          <>
              <button
              onClick={handlePreviousImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-elevation-2 hover:bg-background">

                <Icon name="ChevronLeft" size={20} />
              </button>
              <button
              onClick={handleNextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-elevation-2 hover:bg-background">

                <Icon name="ChevronRight" size={20} />
              </button>
            </>
          }

          {/* Zoom Indicator */}
          <div className="absolute top-4 right-4 bg-background/80 backdrop-blur-sm rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex items-center space-x-2 text-sm text-foreground">
              <Icon name="ZoomIn" size={16} />
              <span>Click to zoom</span>
            </div>
          </div>

          {/* Image Counter */}
          {galleryImages?.length > 1 &&
          <div className="absolute bottom-4 left-4 bg-background/80 backdrop-blur-sm rounded-lg px-3 py-2">
              <span className="text-sm font-medium text-foreground">
                {selectedImageIndex + 1} / {galleryImages?.length}
              </span>
            </div>
          }
        </div>
      </div>
      {/* Thumbnail Navigation */}
      {galleryImages?.length > 1 &&
      <div className="flex space-x-3 overflow-x-auto pb-2">
          {galleryImages?.map((image, index) =>
        <button
          key={image?.id}
          onClick={() => setSelectedImageIndex(index)}
          className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
          index === selectedImageIndex ?
          'border-primary shadow-elevation-2' :
          'border-border hover:border-primary/50'}`
          }>

              <Image
            src={image?.url}
            alt={image?.alt}
            className="w-full h-full object-cover" />

            </button>
        )}
        </div>
      }
      {/* Image Actions */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center space-x-4 relative">
          <button
            type="button"
            onClick={handleShareClick}
            className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="Share2" size={16} />
            <span>Share</span>
          </button>

          {showShareMenu && (
            <div
              role="menu"
              className="absolute left-0 top-full mt-2 z-30 w-56 bg-card border border-border rounded-xl shadow-lg overflow-hidden"
              onMouseLeave={() => setShowShareMenu(false)}
            >
              {shareTargets.map((t) => (
                <button
                  key={t.name}
                  type="button"
                  onClick={() => openShare(t.url)}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                >
                  <Icon name={t.icon} size={16} className="text-muted-foreground" />
                  <span>{t.name}</span>
                </button>
              ))}
              <div className="h-px bg-border my-1" />
              <button
                type="button"
                onClick={handleCopyLink}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                <Icon name={copyConfirm ? 'Check' : 'Link'} size={16} className={copyConfirm ? 'text-emerald-500' : 'text-muted-foreground'} />
                <span>{copyConfirm ? 'Link copied' : 'Copy link'}</span>
              </button>
            </div>
          )}
          <button className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <Icon name="Download" size={16} />
            <span>Save</span>
          </button>
        </div>
        
        {isZoomed &&
        <button
          onClick={() => setIsZoomed(false)}
          className="flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-colors">

            <Icon name="ZoomOut" size={16} />
            <span>Reset zoom</span>
          </button>
        }
      </div>
    </div>);

};

export default ProductImageGallery;